<template>
  <div class="container mt-4">
    <Layout ref="layoutRef" :selectedOption="1" />

    <div class="breadcrumb-container">
      <router-link to="/atencion">Atenciones</router-link> &gt;
      <router-link :to="`/atencionform/${p_idAtencion}`">Atención {{ p_numero }}</router-link>
      &gt; Curvas de Crecimiento
    </div>

    <hr />

    <div class="row mb-3 align-items-end">
      <div class="col-md-4">
        <label for="genero" class="form-label">Género:</label>
        <select id="genero" v-model="genero" class="form-control" disabled>
          <option value="M">Masculino</option>
          <option value="F">Femenino</option>
        </select>
      </div>

      <div class="col-md-4">
        <label for="tipoGrafico" class="form-label">Tipo de Gráfico:</label>
        <select id="tipoGrafico" v-model="tipoGrafico" class="form-control">
          <option v-for="(cfg, key) in TIPO_GRAFICO_CONFIG" :key="key" :value="key">
            {{ cfg.label }}
          </option>
        </select>
      </div>

      <div class="col-md-4">
        <button @click="exportToPDF" class="btn btn-outline-danger">
          Exportar a PDF
        </button>
      </div>
    </div>

    <div v-if="cargando" class="text-center my-4">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
    </div>

    <div v-else-if="datosCargados" ref="pdfContent">
      <h3 class="mt-4 mb-3">Curva de Crecimiento — {{ tituloActual }}</h3>

      <!-- Leyenda de colores semáforo -->
      <div class="leyenda-semaforo mb-2">
        <span class="leyenda-item">
          <span class="leyenda-linea" style="background:#639922;"></span> P50 — Mediana
        </span>
        <span class="leyenda-item">
          <span class="leyenda-linea leyenda-dash" style="background:#EF9F27;"></span> P15 / P85 — Zona de atención
        </span>
        <span class="leyenda-item">
          <span class="leyenda-linea leyenda-dash" style="background:#E24B4A;"></span> P3 / P97 — Zona de alerta
        </span>
        <span class="leyenda-item">
          <span class="leyenda-dot" style="background:#888780;"></span> Historial del paciente
        </span>
        <span class="leyenda-item">
          <span class="leyenda-dot" style="background:#185FA5;"></span> Medida actual
        </span>
      </div>

      <apexchart
        type="line"
        :options="chartOptionsActual"
        :series="chartSeriesActual"
        height="580"
      />

      <!-- Nota clínica contextual (solo IMC) -->
      <div v-if="notaClinicaActual" class="nota-clinica mt-2">
        <span class="nota-icono">ℹ</span> {{ notaClinicaActual }}
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import Layout from "@/components/Layout.vue";
import Footer from "@/components/Footer.vue";
import VueApexCharts from "vue3-apexcharts";
import api from "@/services/api";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

//  Paleta semáforo clínico
//     P3/P97 = rojo, P15/P85 = ámbar, P50 = verde
const COLORES_SEMAFORO = ["#E24B4A", "#EF9F27", "#639922", "#EF9F27", "#E24B4A"];

//  Configuración centralizada por tipo de gráfico
const TIPO_GRAFICO_CONFIG = {
  peso: {
    label: "Peso",
    unidad: "kg",
    idBiometria: 5,
    campoRef: "peso",
    coloresPercentil: COLORES_SEMAFORO,
    colorActual: "#185FA5",
    colorHistorial: "#888780",
    notaClinica: null,
  },
  talla: {
    label: "Talla",
    unidad: "cm",
    idBiometria: 6,
    campoRef: "talla",
    coloresPercentil: COLORES_SEMAFORO,
    colorActual: "#185FA5",
    colorHistorial: "#888780",
    notaClinica: null,
  },
  perimCef: {
    label: "Perímetro Cefálico",
    unidad: "cm",
    idBiometria: 12,
    campoRef: "perimCef",
    coloresPercentil: COLORES_SEMAFORO,
    colorActual: "#185FA5",
    colorHistorial: "#888780",
    notaClinica: null,
  },
  imc: {
    label: "IMC - Índice de Masa Corporal",
    unidad: "kg/m²",
    idBiometria: 7,
    campoRef: "imc",
    coloresPercentil: COLORES_SEMAFORO,
    colorActual: "#185FA5",
    colorHistorial: "#888780",
    notaClinica:
      "Las zonas rojas indican riesgo en extremos opuestos: P3 corresponde a bajo peso / desnutrición; P97 corresponde a obesidad.",
  },
};

const PERCENTILES = ["P3", "P15", "P50", "P85", "P97"];

//  Helper: convierte edad a meses
function calcularEdadMeses(years, months, days) {
  return (
    12 * parseInt(years, 10) +
    parseInt(months, 10) +
    Math.round(parseInt(days, 10) / 30)
  );
}

//  Helper: opciones base del gráfico
function crearChartOptions(config) {
  const { label, coloresPercentil, colorHistorial, colorActual } = config;
  return {
    chart: { toolbar: { show: false } },
    xaxis: {
      categories: [],
      title: { text: "Edad (meses)" },
    },
    yaxis: {
      title: { text: label },
    },
    // P50 línea continua más gruesa; P3/P15/P85/P97 punteadas más finas
    stroke: {
      width:     [1.5, 1.5, 2.5, 1.5, 1.5, 2,   2.5],
      dashArray: [4,   4,   0,   4,   4,   0,   0  ],
    },
    markers: {
      size:   [0, 0, 0, 0, 0, 5, 8],
      colors: [...coloresPercentil, colorHistorial, colorActual],
    },
    legend: {
      markers: {
        fillColors: [...coloresPercentil, colorHistorial, colorActual],
      },
    },
    annotations: { xaxis: [], yaxis: [] },
  };
}

export default {
  name: "CurvasCrecimiento",

  components: {
    Layout,
    Footer,
    apexchart: VueApexCharts,
  },

  props: {
    p_idAtencion: { type: Number, required: true },
    p_idPersona:  { type: Number, required: true },
    p_numero:     { type: String, required: true },
    p_sexo:       { type: String, required: true },
    p_EdadYears:  { type: String, required: true },
    p_EdadMonths: { type: String, required: true },
    p_EdadDays:   { type: String, required: true },
  },

  data() {
    return {
      TIPO_GRAFICO_CONFIG,
      genero: this.p_sexo,
      tipoGrafico: "peso",
      datosCargados: false,
      cargando: false,
      chartOptions: {},
      chartSeries: {},
    };
  },

  computed: {
    edadMeses() {
      return calcularEdadMeses(this.p_EdadYears, this.p_EdadMonths, this.p_EdadDays);
    },
    tituloActual() {
      return TIPO_GRAFICO_CONFIG[this.tipoGrafico]?.label ?? "";
    },
    notaClinicaActual() {
      return TIPO_GRAFICO_CONFIG[this.tipoGrafico]?.notaClinica ?? null;
    },
    chartOptionsActual() {
      return this.chartOptions[this.tipoGrafico] ?? {};
    },
    chartSeriesActual() {
      return this.chartSeries[this.tipoGrafico] ?? [];
    },
  },

  mounted() {
    const layout = this.$refs.layoutRef;
    layout.toggleProcesos();
    layout.toggleReportes();
    layout.toggleParametros();
    layout.toggleAyuda();
    this.cargarDatos();
  },

  watch: {
    tipoGrafico(nuevoTipo) {
      if (this.chartSeries[nuevoTipo]) {
        this.datosCargados = true;
        return;
      }
      this.cargarDatos();
    },
  },

  methods: {
    async obtenerHistorial(idPersona, idBiometria) {
      try {
        const { data } = await api.get(
          `/vwatencionbiomaten/idpersona/${idPersona}/idbiometria/${idBiometria}`
        );
        return data;
      } catch (error) {
        console.error("Error al obtener historial:", error);
        return [];
      }
    },

    async obtenerMedidaActual(idAtencion, idBiometria) {
      try {
        const { data } = await api.get(
          `/vwbiomatens/idatencion-idbiometria/${idAtencion}/${idBiometria}`
        );
        return data.length ? data[0].valor : 0;
      } catch (error) {
        console.error("Error al obtener medida actual:", error);
        return 0;
      }
    },

    async cargarDatos() {
      this.datosCargados = false;
      this.cargando = true;

      try {
        const tipo   = this.tipoGrafico;
        const config = TIPO_GRAFICO_CONFIG[tipo];

        // 1. Datos de referencia
        const { data: refData } = await api.get("/vwmedidarefs");
        const refFiltrada = refData.filter(item => item.genero === this.genero);

        // 2. Categorías y series de percentiles
        const categorias = [...new Set(refFiltrada.map(i => i.edad))].sort((a, b) => a - b);
        const seriesPercentil = Object.fromEntries(PERCENTILES.map(p => [p, []]));

        refFiltrada.forEach(item => {
          PERCENTILES.forEach(p => {
            seriesPercentil[p].push(item[`${config.campoRef}${p}`] ?? null);
          });
        });

        // 3. Incluir edad actual si no está
        if (!categorias.includes(this.edadMeses)) {
          categorias.push(this.edadMeses);
          PERCENTILES.forEach(p => seriesPercentil[p].push(null));
        }

        // 4. Arrays del paciente
        const historicoDatos = new Array(categorias.length).fill(null);
        const datosActuales  = new Array(categorias.length).fill(null);

        // 5. Historial
        const historial = await this.obtenerHistorial(this.p_idPersona, config.idBiometria);
        historial.forEach(item => {
          const edadItem = calcularEdadMeses(item.edadAteYears, item.edadAteMonths, item.edadAteDays);
          const idx = categorias.indexOf(edadItem);
          if (idx !== -1) historicoDatos[idx] = item.valor;
        });

        // 6. Medida actual
        const valorActual = await this.obtenerMedidaActual(this.p_idAtencion, config.idBiometria);
        datosActuales[categorias.indexOf(this.edadMeses)] = valorActual;

        // 7. Construir opciones y series con colores del config
        const options = crearChartOptions(config);
        options.xaxis.categories = categorias;
        options.annotations = this.crearAnnotations(
          this.edadMeses,
          valorActual,
          config.unidad,
          config.colorActual
        );

        const series = [
          ...PERCENTILES.map((p, i) => ({
            name: p,
            data: seriesPercentil[p],
            color: config.coloresPercentil[i],
          })),
          { name: `Historial de ${config.label}`, data: historicoDatos, color: config.colorHistorial },
          { name: `${config.label} Actual`,        data: datosActuales,  color: config.colorActual    },
        ];

        // 8. Cache reactivo
        this.chartOptions = { ...this.chartOptions, [tipo]: options };
        this.chartSeries  = { ...this.chartSeries,  [tipo]: series  };

        this.datosCargados = true;
      } catch (error) {
        console.error("Error al cargar datos:", error);
      } finally {
        this.cargando = false;
      }
    },

    // Anotaciones con el color del punto actual del gráfico
    crearAnnotations(x, y, unidad, color) {
      const estilo = {
        borderColor: color,
        label: {
          borderColor: color,
          style: { color: "#fff", background: color },
          strokeDashArray: 5,
        },
      };
      return {
        xaxis: [{ ...estilo, x, label: { ...estilo.label, text: `${x} meses` } }],
        yaxis: [{ ...estilo, y, label: { ...estilo.label, text: `${y} ${unidad}` } }],
      };
    },

    async exportToPDF() {
      const el = this.$refs.pdfContent;
      if (!el) {
        console.warn("Contenido PDF no disponible.");
        return;
      }
      try {
        const canvas = await html2canvas(el);
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "landscape",
          unit: "px",
          format: [canvas.width, canvas.height],
        });
        pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
        pdf.save("curvas_de_crecimiento.pdf");
      } catch (error) {
        console.error("Error al exportar PDF:", error);
      }
    },
  },
};
</script>

<style scoped>
.breadcrumb-container {
  margin-bottom: 15px;
}

h3 {
  text-align: center;
}

.apexcharts-tooltip {
  color: black;
}

/*  Leyenda semáforo  */
.leyenda-semaforo {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
  color: #555;
  justify-content: center;
}

.leyenda-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.leyenda-linea {
  display: inline-block;
  width: 28px;
  height: 3px;
  border-radius: 2px;
}

.leyenda-dash {
  background: repeating-linear-gradient(
    to right,
    currentColor 0px,
    currentColor 5px,
    transparent 5px,
    transparent 9px
  );
}

.leyenda-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

/* Nota clínica (IMC) */
.nota-clinica {
  background: #fff8e1;
  border-left: 4px solid #EF9F27;
  padding: 8px 12px;
  font-size: 13px;
  color: #5a4000;
  border-radius: 0 4px 4px 0;
}

.nota-icono {
  font-weight: bold;
  margin-right: 4px;
}
</style>